import { useState, type FormEvent } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { extractErrorMessage } from "@/lib/api-client";
import { changePassword } from "@/lib/console-api";
import { useAuthStore } from "@/stores/auth-store";

export function AccountPage(): JSX.Element {
  const account = useAuthStore((s) => s.account);
  const signout = useAuthStore((s) => s.signout);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<
    { kind: "success" | "error"; text: string } | null
  >(null);

  const onSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setMessage(null);
    if (newPassword.length < 8) {
      setMessage({
        kind: "error",
        text: "새 비밀번호는 8자 이상이어야 합니다.",
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage({
        kind: "error",
        text: "새 비밀번호 확인이 일치하지 않습니다.",
      });
      return;
    }
    setSubmitting(true);
    try {
      await changePassword({ currentPassword, newPassword });
      // 서버가 모든 refresh token 을 폐기하므로 다음 요청은 401 → 자연스러운 로그아웃 흐름.
      // 사용자에게 명시적으로 안내한 뒤 signout.
      setMessage({
        kind: "success",
        text: "비밀번호를 바꿨어요. 안전을 위해 다시 로그인해 주세요.",
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      // 잠시 노출 후 자동 로그아웃 — 사용자가 메시지를 읽을 시간 확보.
      setTimeout(() => signout(), 1500);
    } catch (err) {
      setMessage({
        kind: "error",
        text: extractErrorMessage(err, "비밀번호 변경에 실패했습니다."),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-8 p-8">
      <header>
        <p className="text-sm text-muted-foreground">계정 설정</p>
        <h1 className="mt-1 text-2xl font-bold tracking-tight">내 계정</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          기본 정보와 비밀번호를 관리합니다.
        </p>
      </header>

      <section className="rounded-lg border border-border bg-card p-6">
        <h2 className="text-lg font-semibold">기본 정보</h2>
        <dl className="mt-4 space-y-3 text-sm">
          <div className="flex flex-col gap-1 sm:flex-row sm:gap-6">
            <dt className="w-24 text-muted-foreground">이메일</dt>
            <dd>{account?.email ?? "—"}</dd>
          </div>
          <div className="flex flex-col gap-1 sm:flex-row sm:gap-6">
            <dt className="w-24 text-muted-foreground">이름</dt>
            <dd>{account?.name?.trim() || "—"}</dd>
          </div>
        </dl>
        <p className="mt-4 text-xs text-muted-foreground">
          이메일/이름 변경 기능은 곧 추가됩니다.
        </p>
      </section>

      <section className="rounded-lg border border-border bg-card p-6">
        <h2 className="text-lg font-semibold">비밀번호 변경</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          변경 후 모든 디바이스에서 다시 로그인해야 합니다.
        </p>
        <form onSubmit={onSubmit} className="mt-5 max-w-md space-y-4">
          <div>
            <label htmlFor="currentPassword" className="text-sm font-medium">
              현재 비밀번호
            </label>
            <Input
              id="currentPassword"
              type="password"
              autoComplete="current-password"
              required
              className="mt-1.5"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="newPassword" className="text-sm font-medium">
              새 비밀번호 <span className="text-muted-foreground">(8자 이상)</span>
            </label>
            <Input
              id="newPassword"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              maxLength={128}
              className="mt-1.5"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="confirmPassword" className="text-sm font-medium">
              새 비밀번호 확인
            </label>
            <Input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              required
              className="mt-1.5"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>
          {message && (
            <p
              className={
                message.kind === "success"
                  ? "rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-sm"
                  : "rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              }
            >
              {message.text}
            </p>
          )}
          <Button type="submit" disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            변경하기
          </Button>
        </form>
      </section>
    </div>
  );
}
